/**
 * Screenshot handoff: PNG of the exact rendered frame (UI is DOM, not part of the canvas), saved
 * with a local-time filename. Uses the Web Share API with files where downloads are awkward
 * (touch devices that support it), otherwise an <a download>. Reports success only after the
 * handoff call completes; otherwise returns a viewable fallback URL.
 */
export interface ScreenshotResult {
  ok: boolean;
  message: string;
  filename?: string;
  /** Object URL the shell can show when direct download is not possible. */
  fallbackUrl?: string;
}

export function screenshotFilename(worldTitle: string, d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const name = worldTitle.replace(/[^A-Za-z0-9]+/g, '');
  return `GBR_${name}_${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}.png`;
}

export async function saveScreenshot(blob: Blob, worldTitle: string): Promise<ScreenshotResult> {
  const filename = screenshotFilename(worldTitle);
  const file = new File([blob], filename, { type: 'image/png' });
  const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (coarse && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: filename });
      return { ok: true, message: 'Shared', filename };
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return { ok: false, message: 'Not saved' };
      // fall through to download
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  if ('download' in a) {
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    try {
      a.click();
      return { ok: true, message: 'Saved', filename };
    } finally {
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    }
  }
  return { ok: false, message: 'Your browser cannot download directly — the image is shown so you can save it.', fallbackUrl: url, filename };
}
